import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS } from '@/lib/permissions'
import {
  describeGaps,
  getOrganizationProfile,
  type OrganizationProfileGap,
} from '@/lib/api/v2/organization-profile'

/**
 * SOLY-TPL-001 MS-05 -- the "incomplete profile" banner.
 *
 * Before this feature the templates carried Kitchy's address, VAT number and bank
 * account, so an organization with an empty profile still produced a plausible
 * invoice -- someone else's. Now the document says what the profile says, so an
 * empty profile produces an invoice with no account to pay into.
 *
 * V1 warns, it does not refuse (scope decision Q4): a badly ordered refusal would
 * block Kitchy in production, and it is the only organization there. The banner
 * stays until the gap is filled -- no "don't show again", because the gap itself
 * does not go away.
 */
export function IncompleteProfileBanner() {
  const { user } = useAuth()
  const organizationId = user?.selectedOrganizationId ?? null
  // Only the people who can fix it are told about it: /settings answers "Accès refusé"
  // without SETTINGS_MANAGE, so an installer was being sent to a wall.
  const canFix = user?.permissions?.includes(PERMISSIONS.SETTINGS_MANAGE) === true

  // The organization travels with the answer rather than beside it: on an
  // organization switch the previous one's banner disappears immediately instead of
  // lingering for the duration of the call.
  const [answer, setAnswer] = useState<{ organizationId: string; gaps: OrganizationProfileGap[] } | null>(null)

  useEffect(() => {
    if (!organizationId || !canFix) return

    let cancelled = false

    getOrganizationProfile(organizationId)
      .then((profile) => {
        if (!cancelled) setAnswer({ organizationId, gaps: profile.missingForDocuments ?? [] })
      })
      .catch(() => {
        // A banner that could not find out says nothing. Shouting "incomplete
        // profile" on a plain 500 would cost the warning its credibility, and that
        // credibility is what makes it useful on the day it is true.
        if (!cancelled) setAnswer({ organizationId, gaps: [] })
      })

    return () => {
      cancelled = true
    }
  }, [organizationId, canFix])

  const gaps = answer && answer.organizationId === organizationId ? answer.gaps : []

  if (gaps.length === 0) return null

  return (
    <Alert variant="warning" className="mb-6" data-testid="dashboard-incomplete-profile-alert">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Vos documents sortent sans {describeGaps(gaps)}</AlertTitle>
      <AlertDescription>
        Vos devis et factures portent l’identité de votre société. Tant qu’il manque{' '}
        {describeGaps(gaps)}, ils sortiront sans.{' '}
        <Link
          to="/settings?section=organization-profile"
          className="font-medium underline underline-offset-4"
          data-testid="dashboard-incomplete-profile-settings-link"
        >
          Compléter le profil
        </Link>
      </AlertDescription>
    </Alert>
  )
}
